import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { getApiUrl } from '../utils/config';

// Create axios instance with dynamic base URL
const api: AxiosInstance = axios.create({
  baseURL: '', // Will be set dynamically in request interceptor
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to set dynamic base URL and add auth token and client ID
api.interceptors.request.use(
  config => {
    // Set the base URL dynamically on each request
    const apiUrl = getApiUrl();
    config.baseURL = `${apiUrl}/api/v1`;
    
    // Log the full URL being called for debugging (disabled in production)
    if (process.env.NODE_ENV === 'development') {
      const fullUrl = `${config.baseURL}${config.url}`;
      console.log('API Request:', config.method?.toUpperCase(), fullUrl);
    }
    
    // For FormData requests, remove the default Content-Type header
    // Let the browser set it automatically with the correct boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add client ID header if available
    // Check localStorage first, then sessionStorage as fallback (for OAuth callbacks)
    let clientId = localStorage.getItem('current_client_id');
    if (!clientId) {
      clientId = sessionStorage.getItem('oauth_client_id');
      if (clientId) {
        console.log('📋 Using client ID from sessionStorage (OAuth callback):', clientId);
        // Restore to localStorage for future requests
        localStorage.setItem('current_client_id', clientId);
      }
    }
    if (clientId) {
      config.headers['X-Client-ID'] = clientId;
    }
    
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling with token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async error => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (refreshToken) {
        try {
          console.log('🔄 Access token expired, attempting refresh...');
          
          // Try to refresh the token
          const refreshResponse = await axios.post(`${getApiUrl()}/api/v1/auth/refresh`, {
            refresh_token: refreshToken,
          });
          
          const { access_token, refresh_token: newRefreshToken } = refreshResponse.data;
          
          // Store new tokens
          localStorage.setItem('access_token', access_token);
          if (newRefreshToken) {
            localStorage.setItem('refresh_token', newRefreshToken);
          }
          
          console.log('✅ Token refreshed successfully');
          
          // Update the authorization header and retry the original request
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
          
        } catch (refreshError) {
          console.error('❌ Token refresh failed:', refreshError);
          // Refresh failed, clear tokens and redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          
          // Only redirect if not already on the login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      } else {
        // No refresh token available, clear tokens and redirect
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;